import React from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItem,
  TextField,
  Typography,
} from "@mui/material";
import { ChevronDown } from "@tiny/icons";
import { ActivityOption } from "../../TriggerForm.types";

interface ActivitySelectorProps {
  value: string;
  onChange: (value: string) => void;
  options: ActivityOption[];
  onInputChange: (searchText: string) => void;
  isLoading: boolean;
  hasMore?: boolean;
  onScrollEnd?: () => void;
  placeholder?: string;
  label?: string;
  enableInfiniteScroll?: boolean;
}

const ActivitySelector: React.FC<ActivitySelectorProps> = ({
  value,
  onChange,
  options,
  onInputChange,
  isLoading,
  hasMore = false,
  onScrollEnd,
  placeholder = "Search Activity",
  label = "Activity",
  enableInfiniteScroll = false,
}) => {
  const selectedOption = options.find((option) => option.id === value) || null;

  const handleListboxScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollHeight -
        listboxNode.scrollTop -
        listboxNode.clientHeight <
        50 &&
      hasMore &&
      !isLoading &&
      onScrollEnd
    ) {
      onScrollEnd();
    }
  };

  return (
    <FormControl fullWidth>
      <InputLabel
        sx={(theme) => ({
          backgroundColor: theme.palette.background.paper,
          px: 2,
        })}
        shrink
      >
        {label}
      </InputLabel>
      <Autocomplete
        options={options || []}
        value={selectedOption}
        loading={isLoading}
        getOptionLabel={(option) => option?.name || ""}
        isOptionEqualToValue={(option, val) => option.id === val?.id}
        filterSelectedOptions
        popupIcon={<ChevronDown />}
        onChange={(_, newValue) => onChange(newValue?.id || "")}
        onInputChange={(_, newInputValue, reason) => {
          if (reason === "input") {
            onInputChange(newInputValue);
          }
        }}
        ListboxProps={
          enableInfiniteScroll
            ? {
                onScroll: handleListboxScroll,
                style: { maxHeight: 200, overflowY: "auto" },
              }
            : undefined
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            size="small"
            variant="outlined"
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.id}>
            <Typography variant="caption">{option.name}</Typography>
          </Box>
        )}
        ListboxComponent={(props) => (
          <ul {...props}>
            {props.children}
            {isLoading && (
              <ListItem sx={{ justifyContent: "center", py: 1 }}>
                <CircularProgress size={20} />
              </ListItem>
            )}
            {!isLoading &&
              !hasMore &&
              options.length > 0 &&
              enableInfiniteScroll && (
                <ListItem sx={{ justifyContent: "center", py: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    No more results
                  </Typography>
                </ListItem>
              )}
          </ul>
        )}
      />
    </FormControl>
  );
};

export default ActivitySelector;
